import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import ProSkillManagerApi from '~~/package/apis/ProSkillManager/ProSkillManagerApi';
import type {
    ProSkillManagerAssessmentModel
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentModel';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import type {
    ProSkillManagerCandidateModel
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel';
import {ProSkillManagerType} from '~~/package/mapper/proSkillManager/model/ProSkillManagerType';
import {ProSkillMangerReportModel} from '~~/package/mapper/proSkillManager/model/report/ProSkillManagerReportModel';
import {
    ProSkillManagerSubmissionModel
} from '~~/package/mapper/proSkillManager/model/submission/ProSkillManagerSubmissionModel';

import {ProSkillManagerSummaryModel} from '~~/package/mapper/proSkillManager/model/summary/ProSkillManagerSummaryModel';
import {
    ProSkillManagerAssessmentDetailInfoModel
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentDetailInfoModel';
import type ProSkillManagerTrackResponse from '~~/package/apis/ProSkillManager/response/ProSkillManagerTrackResponse';
import {
    ProSkillManagerTimelineModel
} from '~~/package/mapper/proSkillManager/model/actions/ProSkillManagerTimelineModel';
import type CustomResponse from '~~/package/types/ResponseType';
import {
    ProSkillManagerAssessmentCandidateSummaryModel
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerAssessmentCandidateSummaryModel';
import {PageableModel} from '~~/package/mapper/common/model/PageableModel';

export default class ProSkillManagerMapper {
    protected proSkillManagerApi : ProSkillManagerApi;

    constructor(context: ApplicationContextInterface) {
        this.proSkillManagerApi =  context.getService<ProSkillManagerApi>(DependencyInjectId.ProSkillManagerApi)
    }

    public async getProSkillManagerAssessment(lang: string, page: number, itemSize: number, type?: ProSkillManagerType, filters?: string[], title?: string): Promise<PageModel<ProSkillManagerAssessmentModel>> {
        const insightsResponse: CustomResponse<PageModel<ProSkillManagerAssessmentModel>> = (await this.proSkillManagerApi.getProSkillManagerAssessment(lang, page, itemSize, type, filters, title)).data;
        return PageModel.create(insightsResponse.data);
    }

    public async getProSkillManagerAssessmentDetailInfo(id: number): Promise<ProSkillManagerAssessmentDetailInfoModel> {
        const insightsResponse: CustomResponse<ProSkillManagerAssessmentDetailInfoModel> = (await this.proSkillManagerApi.getProSkillManagerAssessmentDetailInfo(id)).data;
        return ProSkillManagerAssessmentDetailInfoModel.create(insightsResponse.data);
    }

    public async getProSkillManagerCandidate(lang: string, page: number, size: number, sort?: string, assessmentId?:number, filter?: string, status?: string[]): Promise<PageModel<ProSkillManagerCandidateModel>> {
        const insightsResponse: CustomResponse<PageModel<ProSkillManagerCandidateModel>> = (await this.proSkillManagerApi.getProSkillManagerCandidate(lang, page, size, sort, assessmentId, filter, status)).data;
        return PageModel.create(insightsResponse.data);
    }

    public async getProSkillManagerCandidateSummary(lang: string, assessmentId: number): Promise<ProSkillManagerAssessmentCandidateSummaryModel> {
        const insightsResponse: CustomResponse<ProSkillManagerAssessmentCandidateSummaryModel> = (await this.proSkillManagerApi.getProSkillManagerCandidateSummary(lang, assessmentId)).data;
        return ProSkillManagerAssessmentCandidateSummaryModel.create(insightsResponse.data);
    }

    public async getProSkillManagerTimeline(assessmentId: number, userUUID: string): Promise<ProSkillManagerTimelineModel> {
        const insightsResponse: CustomResponse<ProSkillManagerTimelineModel> = (await this.proSkillManagerApi.getProSkillManagerTimeline(assessmentId, userUUID)).data;
        return ProSkillManagerTimelineModel.create(insightsResponse.data);
    }

    public async getProSkillManagerReport(lang: string, userUUID: string, assessmentId: number): Promise<ProSkillMangerReportModel> {
        const insightsResponse: CustomResponse<ProSkillMangerReportModel> = (await this.proSkillManagerApi.getProSkillManagerReport(lang, userUUID, assessmentId)).data;
        return ProSkillMangerReportModel.create(insightsResponse.data);
    }

    public async getProSkillManagerSubmission(lang: string, userUUID: string, assessmentId: number, testTemplateId: number): Promise<ProSkillManagerSubmissionModel> {
        const insightsResponse: CustomResponse<ProSkillManagerSubmissionModel> = (await this.proSkillManagerApi.getProSkillManagerSubmission(lang, userUUID, assessmentId, testTemplateId)).data;
        return ProSkillManagerSubmissionModel.create(insightsResponse.data);
    }

    public async getProSkillManagerSummary(timeUnit: string, timeAmount: number, type?: ProSkillManagerType, excludeStat?: boolean): Promise<ProSkillManagerSummaryModel> {
        const response: CustomResponse<ProSkillManagerSummaryModel> = (await this.proSkillManagerApi.getProSkillManagerSummary(timeUnit, timeAmount, type, excludeStat)).data;
        return ProSkillManagerSummaryModel.create(response.data);
    }

    public async getProSkillManagerTrack(type?: ProSkillManagerType): Promise<ProSkillManagerTrackResponse> {
        const response: CustomResponse<ProSkillManagerTrackResponse> = (await this.proSkillManagerApi.getProSkillManagerTrack(type)).data;
        return response.data;
    }

    public async getAssessmentTasks(customAssessmentId: number): Promise<Array<{trackTitle: string, trackLevelTitle: string, taskIds: number[]}>> {
        const response: CustomResponse<Array<{trackTitle: string, trackLevelTitle: string, taskIds: number[]}>> = (await this.proSkillManagerApi.getAssessmentTasks(customAssessmentId)).data;
        return response.data;
    }

    public async getPromptHistory(userUUID: string, domain: string, domainId: string | number, taskId: string | number): Promise<{agentInstruction: string | null; items: Array<{ts: number; message: string | null; response: string | null; promptToken: number | null; totalToken: number | null; status: string | null}>}> {
        const res: CustomResponse<{agentInstruction: string | null; items: Array<{ts: number; message: string | null; response: string | null; promptToken: number | null; totalToken: number | null; status: string | null}>}> = (await this.proSkillManagerApi.getPromptHistory(userUUID, domain, domainId, taskId)).data;
        return res.data;
    }
}
